import cookie from 'cookiejs';
import { LEFT_FLAG } from './returnGuard';
import { sleep } from './sleep';

// Cap for the awaited deep wipe: teardown must never stall behind a hung IndexedDB
// delete or a slow push-revocation round-trip.
export const DEEP_WIPE_CAP_MS = 1500;

const wipeSyncStorages = () => {
  // Survive the wipe: `redirect` (permanent safe-page poison) and `in6to_left` (the
  // Back/Forward return guard armed on unload). Both must outlive the sessionStorage
  // clear so the head-script can still bounce a return to the app.
  const shouldRedirect = !!sessionStorage.getItem('redirect');
  const leftFlag = sessionStorage.getItem(LEFT_FLAG);

  console.clear();
  localStorage.clear();
  sessionStorage.clear();
  cookie.clear();

  if (shouldRedirect) sessionStorage.setItem('redirect', 1);
  if (leftFlag) sessionStorage.setItem(LEFT_FLAG, leftFlag);
};

const deleteAllIndexedDbs = () =>
  indexedDB.databases().then((dbs) =>
    Promise.all(
      dbs.map(
        (db) =>
          new Promise((resolve) => {
            const request = indexedDB.deleteDatabase(db.name);
            request.onsuccess = resolve;
            request.onerror = resolve;
            // A blocked deletion completes once the holding connection closes (the
            // reload); waiting for that here would stall the wipe cap.
            request.onblocked = resolve;
          })
      )
    )
  );

const clearCacheStorage = () =>
  'caches' in window
    ? caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
    : Promise.resolve();

const unregisterServiceWorkers = () =>
  // Not serviceWorker.ready — it never resolves when no worker is registered.
  navigator.serviceWorker?.getRegistrations
    ? navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.all(registrations.map((r) => r.unregister())))
    : Promise.resolve();

// Push revoke first (deleteToken needs a live SW registration), then drop the workers.
const revokePushThenUnregisterSw = () =>
  Promise.resolve(window.App?.revokePushSubscription?.())
    .catch(() => {})
    .then(unregisterServiceWorkers);

// Sync best-effort wipe for unload paths (beforeunload can't await).
export const wipeOutTraces = () => {
  deleteAllIndexedDbs().catch(() => {});
  wipeSyncStorages();
};

// Awaited deep wipe for the signout thunks: additionally clears Cache Storage, revokes
// the push subscription and unregisters service workers, capped to keep teardown fast.
export const wipeOutTracesDeep = ({ timeoutMs = DEEP_WIPE_CAP_MS } = {}) => {
  const wipe = Promise.allSettled([
    deleteAllIndexedDbs(),
    clearCacheStorage(),
    revokePushThenUnregisterSw(),
  ]);
  wipeSyncStorages();

  return Promise.race([wipe, sleep(timeoutMs)]);
};
